import superagentPromise from 'superagent-promise';
import _superagent from 'superagent';

import config from '@/../script/config.json';
import { b64EncodeUnicode, getBaseDate, getBaseCurrency, getBuyer, getBuyerId, getLocale } from '@/utils/utils';
import { getEventBus } from '@/plugin/eventBus';

let token = null;
const appEl = document.getElementById('app');
const lang = getLocale();
const { ptoken, env } = getBaseDate(appEl);
const API_ROOT = config.api[env];
const superagent = superagentPromise(_superagent, global.Promise);

const currency = getBaseCurrency;

const getBuyerToken = () => {
  const buyer = getBuyer();
  const buyerToken = buyer && buyer.buyerToken;
  // multiplying buyer.buyerTokenExpiresAt by 1000 because it's a timestamp with second precision and Date expects milliseconds
  const buyerTokenExpiresAt = buyer && buyer.buyerTokenExpiresAt && new Date(buyer.buyerTokenExpiresAt * 1000);

  if (buyerToken && buyerTokenExpiresAt > new Date()) {
    return buyerToken;
  }
  return null;
};
const extractResponseBody = res => res.body;
const tokenPlugin = (req) => {
  if (token) {
    req.set('Session', token);
  }
};
const buyerTokenPlugin = (req) => {
  const buyerToken = getBuyerToken();
  if (buyerToken) {
    req.set('X-Buyer-Session', buyerToken);
  }
};
const handleBuyerSessionErrors = (res) => {
  const buyerSessionExpired = res.status === 400 && res.body.error && res.body.error.code === 'E_ACCESSDENY';
  if (buyerSessionExpired) {
    getEventBus().$emit('auth__session_expired', res);
  }
  return res;
};

const requests = {
  get: ({ url, query = {}, withBuyerSession = false, withCurrency = false }) => {
    let request = superagent
      .get(`${API_ROOT}${url}`)
      .query({ lang })
      .query(query)
      .use(tokenPlugin);
    if (withCurrency) {
      request = request.query({ currency: currency() });
    }
    if (withBuyerSession) {
      request = request.use(buyerTokenPlugin);
    }
    return request
      .responseType('json')
      .ok(res => res.status < 500)
      .then(res => (withBuyerSession ? handleBuyerSessionErrors(res) : res))
      .then(extractResponseBody);
  },
  getBlob: ({ url, withBuyerSession = false }) => {
    let request = superagent
      .get(`${API_ROOT}${url}`)
      .query({ lang })
      .use(tokenPlugin);
    if (withBuyerSession) {
      request = request.use(buyerTokenPlugin);
    }
    return request
      .responseType('blob')
      .ok(res => res.status < 500)
      .then(res => (withBuyerSession ? handleBuyerSessionErrors(res) : res));
  },
  post: ({ url, params, withBuyerSession = false, withCurrency = false }) => {
    const requestParams = Object.assign({}, params, { lang: getLocale() }, withCurrency ? { currency: currency() } : {});
    let request = superagent
      .post(`${API_ROOT}${url}`)
      .type('form')
      .send(requestParams)
      .use(tokenPlugin);

    if (withBuyerSession) {
      request = request.use(buyerTokenPlugin);
    }
    return request
      .ok(res => res.status < 500)
      .then(res => (withBuyerSession ? handleBuyerSessionErrors(res) : res))
      .then(extractResponseBody);
  },
};

const Content = {
  cities: () =>
    requests.get({ url: '/cities', query: { wcountry: 1 }, withCurrency: true }),
  promo: (promocode, id, buyerid) =>
    requests.get({ url: '/checkpromo', query: { promocode, id, buyerid }, withCurrency: true }),
  doctypes: () =>
    requests.get({ url: '/doctypes' }),
  countries: () =>
    requests.get({ url: '/countries' }),
  exportPDF: ({ buyId, justBought = false }) =>
    // tickets are bought outside of the buyer session (see Buy.buyinit and Buy.buysite), so we retrieve PDFs
    // for newly bought tickets outside of the session as well to make sure that the result does not depend on
    // the session state (i.e. whether session is still valid or already expired)
    requests.getBlob({ url: `/exportPDF?buyid=${buyId}`, withBuyerSession: !justBought }),
  currencyList: () =>
    requests.get({ url: '/currlist' }),
  buyertickets: id =>
    requests.get({ url: '/buyertickets', query: { id }, withBuyerSession: true }),
  setrefundreq: refundRequest =>
    requests.post({ url: '/setrefundreq', params: { data: b64EncodeUnicode(JSON.stringify(refundRequest)) }, withBuyerSession: true }),
};

const AuthServer = {
  login: () =>
    requests.get({ url: '/login', query: { public: ptoken } }),
  frameparams: () =>
    requests.get({ url: '/frameparams' }),
};

const Authentication = {
  signin: (login, password) =>
    requests.post({ url: '/buyerlogin', params: { login, password } }),
  signup: profile =>
    requests.post({ url: '/buyerreg', params: { data: b64EncodeUnicode(JSON.stringify(profile)) } }),
  user: id =>
    requests.get({ url: '/buyerget', query: { id }, withBuyerSession: true }),
  update: (id, profile) =>
    requests.post({ url: '/buyerset', params: { id, data: b64EncodeUnicode(JSON.stringify(profile)) }, withBuyerSession: true }),
  recoveryEmail: (email, url) =>
    requests.post({ url: '/buyerpwsend', params: { email, url } }),
  recoveryCheck: checkcode =>
    requests.post({ url: '/buyerpwcheck', params: { checkcode } }),
  resetPassword: (checkcode, password) =>
    requests.post({ url: '/buyerpwreset', params: { checkcode, password } }),
};

const Trip = {
  search: params =>
    requests.get({ url: `/trips${params}`, query: { buyerid: getBuyerId() }, withCurrency: true }),
  route: id =>
    requests.get({ url: '/route', query: { id }, withCurrency: true }),
  info: (id, passcount) =>
    requests.get({ url: '/trip', query: { id, passcount, buyerid: getBuyerId() }, withCurrency: true }),
};

const Buy = {
  buyinit: () =>
    requests.get({ url: '/buyinit' }),
  buysite: ({ buyid, id, reserveMode, places, paymentSystem, returnUrl, buyerid, copy }) =>
    // can be executed with and without buyer session
    requests.post({
      url: '/buysite',
      params: {
        buyid,
        id,
        buyerid,
        reserve: reserveMode,
        places: b64EncodeUnicode(JSON.stringify(places)),
        paysys: paymentSystem,
        returnurl: returnUrl,
        copy,
      },
      withBuyerSession: true,
      withCurrency: true,
    }),
  getOrder: id =>
    // the request is for getting the order after payment so we want to call it outside of the session
    // to make sure that the result does not depend on the session state (i.e. whether session is still valid or already expired)
    requests.post({ url: '/buysite', params: { buyid: id, copy: 1 } }),
};

export function setToken(publicToken) {
  token = publicToken;
}

export default {
  Content,
  AuthServer,
  Trip,
  Buy,
  Authentication,
};



// WEBPACK FOOTER //
// ./src/api/index.js